import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { RestApiService } from "./rest-api.service";
import { AuthCredential, EmailCodeVerifyItem, EmailRequestCodeItem, EmailVerifyItem, ExternalUser, ServiceToken } from "../models/auth-data.model";

// auth/captcha: the app's reCAPTCHA set-up for login (site keys only, never secrets)
export interface CaptchaConfig {
    enabled: boolean;
    required: boolean;
    v3: { site_key: string; action: string } | null;
    v2: { site_key: string } | null;
}

@Injectable({
    providedIn: 'root'
})
export class ApiAuthService extends RestApiService {

    /**
     * Which reCAPTCHA the sign-in page should show for this app (site keys only).
     * Public: called before anyone is signed in.
     */
    public apiGetCaptchaConfig(): Observable<CaptchaConfig> {
        return this.getRaw('auth/captcha/', { client_id: this.getClientID() });
    }

    /**
     * Email/password login
     * Automatically adds grant_type and client_id to payload
     */
    public apiPostEmailLogin(payload: any, params: any): Observable<AuthCredential> {
        payload['grant_type'] = 'password';
        payload['client_id'] = this.getClientID();
        return this.postSingle('auth/token/', payload, AuthCredential.parse, params);
    }

    /**
     * Logout user
     * Automatically adds client_id to payload
     */
    public apiPostLogout(payload: any, params: any): Observable<any> {
        payload['client_id'] = this.getClientID();
        return this.postRaw('auth/logout/', payload, params);
    }

    /**
     * Verify email address
     */
    public apiPostEmailVerification(payload: any, params: any): Observable<EmailVerifyItem> {
        return this.postSingle('auth/email/verification/', payload, EmailVerifyItem.parse, params);
    }

    /**
     * Request pre-signup verification code
     */
    public apiPostEmailPreSignUp(payload: any, params: any): Observable<EmailRequestCodeItem> {
        return this.postSingle('auth/email/presignup/', payload, EmailRequestCodeItem.parse, params);
    }

    /**
     * Verify pre-signup code
     */
    public apiPostEmailPreSignUpVerification(payload: any, params: any): Observable<EmailCodeVerifyItem> {
        return this.postSingle('auth/email/presignup-verification/', payload, EmailCodeVerifyItem.parse, params);
    }

    /**
     * Request password reset code
     */
    public apiPostEmailResetPasswordCode(payload: any, params: any): Observable<EmailRequestCodeItem> {
        return this.postSingle('auth/email/reset-password-code/', payload, EmailRequestCodeItem.parse, params);
    }

    /**
     * Verify password reset code
     */
    public apiPostEmailResetPasswordCodeVerification(payload: any, params: any): Observable<EmailCodeVerifyItem> {
        return this.postSingle('auth/email/reset-password-code-verification/', payload, EmailCodeVerifyItem.parse, params);
    }

    /**
     * Reset password
     */
    public apiPostEmailResetPassword(payload: any, params: any): Observable<any> {
        return this.postRaw('auth/email/reset-password/', payload, params);
    }

    /**
     * Email signup
     */
    public apiPostEmailSignUp(payload: any, params: any): Observable<any> {
        return this.postRaw('auth/email/signup/', payload, params);
    }

    /**
     * Fast email signup
     */
    public apiPostFastEmailSignUp(payload: any, params: any): Observable<any> {
        return this.postRaw('auth/email/fast-signup/', payload, params);
    }

    /**
     * Refresh authentication token
     * Automatically adds grant_type and client_id to payload
     */
    public apiPostRefreshToken(payload: any, params: any): Observable<AuthCredential> {
        payload['grant_type'] = 'refresh_token';
        payload['client_id'] = this.getClientID();
        return this.postSingle('auth/token/', payload, AuthCredential.parse, params);
    }

    /**
     * Firebase authentication
     * Automatically adds client_id to payload
     */
    public apiPostFirebaseAuth(payload: any, params: any): Observable<AuthCredential> {
        payload['client_id'] = this.getClientID();
        return this.postSingle('firebase/login/', payload, AuthCredential.parse, params);
    }

    /**
     * Link Firebase account inline
     */
    public apiPostFirebaseInlineLink(payload: any, params: any): Observable<ExternalUser> {
        return this.postSingle('firebase/inline-link/', payload, ExternalUser.parse, params);
    }

    /**
     * Get Firebase custom token
     */
    public apiPostFirebaseCustomToken(payload: any, params: any): Observable<any> {
        return this.postRaw('firebase/custom-token/', payload, params);
    }

}

@Injectable({
    providedIn: 'root'
})
export class ApiServiceTokenService extends RestApiService {

    /**
     * Create a new service token
     */
    public apiPostObject(payload: any, params: any): Observable<ServiceToken> {
        return this.postSingle('service-tokens/', payload, ServiceToken.parse, params);
    }

    /**
    * Create a url
    */
    public apiPostExternalUrl(payload: any, params: any): Observable<ServiceToken> {
        return this.postRaw('service-tokens/external-url/', payload, params);
    }

}

